import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { fetchCharacters } from "@/lib/api";
import { COLOR_MAP } from "@/lib/constants";
import SectionHeading from "@/components/common/SectionHeading";
import LoadingGrid from "@/components/common/LoadingGrid";
import { cn } from "@/lib/utils";

// Colored-spike labels straight from the book jacket
const SPIKE_LABEL = {
  gold: "Yellow spikes",
  red: "Red spikes",
  blue: "Blue spikes",
  green: "Green spikes",
  purple: "Nightmare violet",
};

function HeroTile({ character }) {
  const c = COLOR_MAP[character.color] || COLOR_MAP.gold;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.35, ease: "easeOut" }}
    >
      <Card
        className={cn(
          "relative overflow-hidden rounded-2xl border bg-[hsl(var(--kotz-ink-2))]",
          c.border
        )}
        data-testid={`storybook-hero-${character.slug}`}
      >
        <div className="aspect-[4/5] w-full overflow-hidden bg-black">
          <img
            src={character.image_url}
            alt={character.name}
            className="w-full h-full object-cover object-center"
            loading="lazy"
          />
          <div className={cn("absolute inset-0 pointer-events-none", c.glow)} />
        </div>
        <div className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-display text-xl tracking-wider text-foreground">
                {character.name.toUpperCase()}
              </p>
              <p className="text-xs text-foreground/60 mt-0.5">{character.role}</p>
            </div>
            <Badge
              variant="outline"
              className={cn(
                "font-mono text-[10px] uppercase tracking-[0.2em] shrink-0",
                c.border,
                c.text,
                c.bgSoft
              )}
            >
              {SPIKE_LABEL[character.color] || character.color}
            </Badge>
          </div>
          <p className="mt-2 text-xs text-foreground/60 line-clamp-2">{character.tagline}</p>
        </div>
      </Card>
    </motion.div>
  );
}

export default function HeroesFromBookSection() {
  const { data: characters = [], isLoading } = useQuery({
    queryKey: ["characters", "heroes"],
    queryFn: () => fetchCharacters("heroes"),
  });

  return (
    <section className="relative py-20 bg-[hsl(var(--kotz-ink))]">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Meet the Heroes"
          title="THE KOTZINONS OF THE BOOK"
          subtitle="Five superheroes — each with their own colored spikes and their own way of protecting the ones they love."
          align="center"
          className="mx-auto mb-10"
        />

        {isLoading ? (
          <LoadingGrid count={5} className="sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5" />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
            {characters.map((character) => (
              <HeroTile key={character.id} character={character} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
